import { createLazyComponent, type LazyComponent, LazyAction, LazyModule } from 'owa-bundling';
import type * as lazyIndex from './lazyIndex';
import type { AgentJobSurfaceTileContentProps } from './components/AgentJobSurfaceTileContent';
import type { AgentJobsDrawerPanelProps } from './components/AgentJobsDrawerPanel';
import type { AgentJobsPanelProps } from './components/AgentJobsPanel';
import type { AgentJobsSectionBodyProps } from './components/AgentJobsEmbedded';

const lazyModule: LazyModule<typeof lazyIndex> = new LazyModule(
    () => import(/* webpackChunkName: "CalendarAiLabsAgentJobsPanel" */ './lazyIndex'),
    { name: 'CalendarAiLabsAgentJobsPanel' }
);

export const AgentJobsDrawerPanel: LazyComponent<AgentJobsDrawerPanelProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.AgentJobsDrawerPanel);

// The bare panel body (no right-rail drawer chrome, no isLabsBarActive /
// "yield to Copilot chat" gating). Exposed so hosts that already live inside the
// Copilot chat drawer — e.g. the Tasks Hub — can embed the agent-jobs UI
// directly; AgentJobsDrawerPanel returns null in that context by design.
export const AgentJobsPanel: LazyComponent<AgentJobsPanelProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.AgentJobsPanel);

// Embedded Tasks Hub section bodies. Each owns an agent-jobs controller
// instance (the primary one mounts the shared dialogs + view telemetry) and
// fills a Tasks Hub AccordionPanel; Tasks Hub owns the section chrome. The host
// drawer passes these as opaque section descriptors. Both resolve from the same
// panel chunk.
export const AgentJobsWorkflowsBody: LazyComponent<AgentJobsSectionBodyProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.AgentJobsWorkflowsBody);

export const AgentJobsAutomationsBody: LazyComponent<AgentJobsSectionBodyProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.AgentJobsAutomationsBody);

export const AgentJobsSurfaceHost: LazyComponent<{}, unknown, void> = createLazyComponent(
    lazyModule,
    m => m.AgentJobsSurfaceHost
);

export const AgentJobSurfaceTileContent: LazyComponent<
    AgentJobSurfaceTileContentProps,
    unknown,
    void
> = createLazyComponent(lazyModule, m => m.AgentJobSurfaceTileContent);

// Activation entry for agent-job surface tiles (open output drawer). Invoked by the
// calendar surface click/keyboard handlers; the panel chunk is already loaded whenever
// agent tiles are projected onto the grid, so this resolves without perceptible delay.
export const lazyOpenAgentJobOutput: LazyAction<
    typeof lazyIndex.openAgentJobOutput,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.openAgentJobOutput);
